/**
 * El catálogo de planes, en el frontend.
 *
 * Espejo de `core/planes.py` en el backend, que es quien manda: aquí solo vive lo
 * que hay que PINTAR (nombre, precio, qué incluye). Los topes de socios se repiten
 * porque el aviso de fin de prueba tiene que nombrar el plan que toca sin pedir nada
 * al servidor, pero quien decide de verdad es el backend.
 *
 * Spec: planes/precios-tres-planes/spec.md (APROBADA 12-09-2026).
 *
 * `name` es el nombre CORTO y único de cada plan («Básico», «Club Pro»). Había dos
 * juegos de nombres —«Premium» en la ficha de tienda, «Plan Premium» en Ajustes— y
 * eso es la misma duplicación que este cambio viene a quitar: un dato, un render.
 *
 * Los cuatro tramos viejos siguen aquí porque **hay clubes con ellos**: si un club en
 * `estandar` abre Ajustes, su plan tiene que tener nombre. Se marcan `vigente: false`
 * — se pintan si los tienes, no se ofrecen a nadie nuevo (RF-001).
 */

export const PLANES = [
  {
    id: "prueba",
    rank: 0,
    vigente: true,
    name: "Prueba",
    price: 0,
    setupFee: 0,
    // Sin tope a propósito: el límite de la prueba es el tiempo, no el tamaño (RF-003).
    maxSocios: null,
    sociosLabel: "Todos tus socios, sin límite",
    iaPromptsLabel: "Asistente IA incluido",
    upgradeLabel: "Empezar la prueba",
  },
  {
    id: "club",
    rank: 1,
    vigente: true,
    name: "Club",
    price: 99,
    setupFee: 0,
    maxSocios: 1000,
    sociosLabel: "Hasta 1.000 socios",
    iaPromptsLabel: "Asistente IA incluido",
    upgradeLabel: "Cambiar a Club",
  },
  {
    id: "club_pro",
    rank: 2,
    vigente: true,
    name: "Club Pro",
    price: 199,
    setupFee: 0,
    maxSocios: null,
    sociosLabel: "Sin límite de socios",
    iaPromptsLabel: "Asistente IA incluido (uso avanzado)",
    upgradeLabel: "Cambiar a Club Pro",
  },
  // ── Los de antes: se pintan si los tienes, no se ofrecen (RF-001) ──────────
  { id: "test", rank: 90, vigente: false, name: "Test", price: 10, setupFee: 10, maxSocios: 25, sociosLabel: "25 socios", iaPromptsLabel: "Asistente IA incluido (uso de prueba)", upgradeLabel: "Test" },
  { id: "basico", rank: 91, vigente: false, name: "Básico", price: 29.99, setupFee: 50, maxSocios: 250, sociosLabel: "250 socios", iaPromptsLabel: "Asistente IA incluido (uso esencial)", upgradeLabel: "Básico" },
  { id: "estandar", rank: 92, vigente: false, name: "Estándar", price: 59.99, setupFee: 100, maxSocios: 750, sociosLabel: "750 socios", iaPromptsLabel: "Asistente IA incluido (uso intensivo)", upgradeLabel: "Estándar" },
  { id: "premium", rank: 93, vigente: false, name: "Premium", price: 99.99, setupFee: 200, maxSocios: null, sociosLabel: "Sin límite de socios", iaPromptsLabel: "Asistente IA incluido (uso avanzado)", upgradeLabel: "Premium" },
];

/** Lo que se le ofrece a un club: Prueba, Club y Club Pro. */
export const PLANES_VIGENTES = PLANES.filter((p) => p.vigente);

export const PLAN_POR_ID = PLANES.reduce((acc, p) => ({ ...acc, [p.id]: p }), {});

export function plan(id) {
  return PLAN_POR_ID[id] || null;
}

export function nombrePlan(id) {
  return plan(id)?.name || "—";
}

/**
 * El plan que le toca a un club por su número de socios (RF-007).
 *
 * El corte es por socios y nada más: la lista de funciones de la landing es texto
 * comercial y no gatea nada (decidido 12-09). Sirve para que el aviso de fin de
 * prueba diga «tienes 1.500 socios: te toca Club Pro» en vez de «elige un plan
 * abajo», que es lo que decía y no le contaba a nadie cuánto iba a pagar.
 */
export function planRecomendado(sociosCount) {
  const n = Number(sociosCount || 0);
  const club = plan("club");
  return n > club.maxSocios ? plan("club_pro") : club;
}

export function formatearEuros(value) {
  const n = Number(value || 0);
  // Los planes nuevos son enteros (99, 199): "99 €" se lee mejor que "€99.00".
  // Los viejos llevan céntimos y hay que respetarlos.
  return Number.isInteger(n) ? `${n} €` : `${n.toFixed(2)} €`;
}
